/* ─────────────────────────────────────────────────────────────────────────────
   PGA Yorkshire — Global Scroll Reveal
   ─────────────────────────────────────────────────────────────────────────────

   HOW TO USE IN ANY SECTION
   ─────────────────────────
   Single element (animates independently):
     <h2 data-reveal>Heading</h2>
     <p  data-reveal data-reveal-delay="0.12">Body copy</p>

   Direction (default = "up"):
     data-reveal="up"    — slides in from below   (exit: slides up-left)
     data-reveal="left"  — slides in from right
     data-reveal="right" — slides in from left
     data-reveal="fade"  — opacity only, no movement

   Staggered group (children fire sequentially as a batch):
     <div data-reveal-group data-reveal-stagger="0.1">
       <span data-reveal-child>Item 1</span>
       <span data-reveal-child>Item 2</span>
     </div>

   Options:
     data-reveal-delay="0.15"    extra seconds before animation fires
     data-reveal-duration="0.7"  animation speed in seconds
     data-reveal-once            prevent exit / re-entry animation (stays visible)
     data-reveal-start="top 80%" custom ScrollTrigger start position

   WHAT IT DOES
   ────────────
   • Enter  → element slides in + fades to full opacity
   • Exit   → element slides up-left + fades out (keeping the page feeling alive)
   • EnterBack → re-animates from below if you scroll back up
   • LeaveBack → re-hides if you scroll back past the entry point

   Exit animation is on by default. Add data-reveal-once to any element
   you want to stay permanently visible after first reveal.
──────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var GSAP_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
  var ST_CDN   = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';

  var CFG = {
    duration:     0.72,
    ease:         'power3.out',
    exitEase:     'power2.in',
    exitDuration: 0.42,
    distance:     48,
    stagger:      0.10,
    start:        'top 88%'
  };

  /* ── Build the "from" state based on direction ── */
  function fromState(el) {
    var dir  = ((el.dataset.reveal || el.dataset.revealDir || 'up')).toLowerCase();
    var dist = parseFloat(el.dataset.revealDistance) || CFG.distance;
    var from = { opacity: 0 };
    if (dir === 'up')    from.y = dist;
    if (dir === 'down')  from.y = -dist;
    if (dir === 'left')  from.x = dist;
    if (dir === 'right') from.x = -dist;
    return from;
  }

  /* ── Enter tween for a single element ── */
  function enterTween(el, delay) {
    return {
      opacity:  1,
      x:        0,
      y:        0,
      duration: parseFloat(el.dataset.revealDuration) || CFG.duration,
      ease:     CFG.ease,
      delay:    delay || 0
    };
  }

  /* ── Wire up a single [data-reveal] element ── */
  function revealEl(el) {
    if (el._srInit) return;
    el._srInit = true;

    var once  = el.hasAttribute('data-reveal-once');
    var delay = parseFloat(el.dataset.revealDelay) || 0;

    gsap.set(el, fromState(el));

    ScrollTrigger.create({
      trigger: el,
      start:   el.dataset.revealStart || CFG.start,

      onEnter: function () {
        gsap.to(el, enterTween(el, delay));
      },

      onLeave: once ? undefined : function () {
        /* Exit upward + slight left drift — the "Zera" feel */
        gsap.to(el, {
          opacity:  0,
          y:        -22,
          x:        -18,
          duration: CFG.exitDuration,
          ease:     CFG.exitEase
        });
      },

      onEnterBack: once ? undefined : function () {
        gsap.to(el, enterTween(el, 0));
      },

      onLeaveBack: once ? undefined : function () {
        /* Re-hide below when scrolling back above the trigger */
        var from = fromState(el);
        gsap.to(el, {
          opacity:  0,
          y:        from.y || 0,
          x:        from.x || 0,
          duration: CFG.exitDuration,
          ease:     CFG.exitEase
        });
      }
    });
  }

  /* ── Wire up a [data-reveal-group] — children stagger as one batch ── */
  function revealGroup(groupEl) {
    if (groupEl._srInit) return;
    groupEl._srInit = true;

    var once    = groupEl.hasAttribute('data-reveal-once');
    var stagger = parseFloat(groupEl.dataset.revealStagger) || CFG.stagger;
    var children = Array.prototype.slice.call(
      groupEl.querySelectorAll('[data-reveal-child]')
    );
    if (!children.length) return;

    /* Set initial hidden state on each child */
    children.forEach(function (child) {
      gsap.set(child, fromState(child));
    });

    ScrollTrigger.create({
      trigger: groupEl,
      start:   groupEl.dataset.revealStart || CFG.start,

      onEnter: function () {
        gsap.to(children, {
          opacity:  1, x: 0, y: 0,
          duration: CFG.duration,
          ease:     CFG.ease,
          stagger:  stagger
        });
      },

      onLeave: once ? undefined : function () {
        gsap.to(children, {
          opacity:  0, y: -18, x: -14,
          duration: CFG.exitDuration,
          ease:     CFG.exitEase,
          stagger:  0.05
        });
      },

      onEnterBack: once ? undefined : function () {
        gsap.to(children, {
          opacity:  1, x: 0, y: 0,
          duration: CFG.duration,
          ease:     CFG.ease,
          stagger:  { each: stagger, from: 'end' }
        });
      },

      onLeaveBack: once ? undefined : function () {
        var reverseChildren = children.slice().reverse();
        gsap.to(reverseChildren, {
          opacity:  0,
          y:        function (i, el) { return fromState(el).y || 0; },
          x:        function (i, el) { return fromState(el).x || 0; },
          duration: CFG.exitDuration,
          ease:     CFG.exitEase,
          stagger:  0.05
        });
      }
    });
  }

  /* ── Scan the DOM for reveal elements ── */
  function init(root) {
    root = root || document;

    /* Groups first */
    root.querySelectorAll('[data-reveal-group]').forEach(function (g) {
      revealGroup(g);
    });

    /* Standalone elements (skip children already handled by a group) */
    root.querySelectorAll('[data-reveal]').forEach(function (el) {
      if (el.closest('[data-reveal-group]')) return;
      revealEl(el);
    });
  }

  /* ── GSAP loader (deduplicates via script tag check) ── */
  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) {
        /* Already injected — may still be loading */
        if (window.gsap && window.ScrollTrigger) { resolve(); return; }
        var existing = document.querySelector('script[src="' + src + '"]');
        existing.addEventListener('load', resolve);
        existing.addEventListener('error', reject);
        return;
      }
      var s = document.createElement('script');
      s.src     = src;
      s.onload  = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function boot(root) {
    if (window.gsap && window.ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
      init(root);
      return;
    }
    loadScript(GSAP_CDN)
      .then(function () { return loadScript(ST_CDN); })
      .then(function () {
        gsap.registerPlugin(ScrollTrigger);
        init(root);
      })
      .catch(function (e) { console.warn('[scroll-reveal] GSAP load failed:', e); });
  }

  /* ── Startup ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Shopify editor: re-run when any section is added or reloaded */
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });

  /* Expose globally so other scripts can call ScrollReveal.refresh() */
  window.ScrollReveal = {
    refresh: function (root) { boot(root || document); }
  };
})();
